"use client";

import { Alert, type AlertProps, Group, Text, Transition } from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconCoinOff,
  IconCoins,
  IconInfoCircle,
} from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import { TopUpCreditsButton } from "@/app/dashboard/account/TopUpCreditsButton";
import { useCreditsBalance } from "@/hooks/use-credits-balance";
import {
  ANALYTICS_EVENTS,
  type InsufficientCreditsAction,
} from "@/lib/analytics/events";

type CustomAlertProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  iconComponent?: React.ReactNode;
} & AlertProps;

export const AlertInfo = ({
  title,
  description,
  children,
  ...rest
}: CustomAlertProps) => {
  const [opened, setOpened] = useState(true);
  const withCloseButton = rest.withCloseButton ?? false;

  if (!opened) {
    return null;
  }

  return (
    <Alert
      variant="light"
      color="blue"
      title={title}
      icon={<IconInfoCircle />}
      withCloseButton={withCloseButton}
      onClose={() => setOpened(false)}
      {...rest}
    >
      {description ? <Text size="md">{description}</Text> : children}
    </Alert>
  );
};

export const AlertWarning = ({
  title,
  description,
  children,
  iconComponent,
  ...rest
}: CustomAlertProps) => {
  const [opened, setOpened] = useState(true);
  const withCloseButton = rest.withCloseButton ?? false;

  if (!opened) {
    return null;
  }

  return (
    <Alert
      variant="light"
      color="yellow"
      title={title}
      icon={iconComponent ?? <IconAlertCircle />}
      withCloseButton={withCloseButton}
      onClose={() => setOpened(false)}
      {...rest}
    >
      {description ? <Text size="md">{description}</Text> : children}
    </Alert>
  );
};

export const AlertCreditCost = ({
  required,
  action,
}: {
  required: number;
  /** What the credits would pay for, reported on `insufficient_credits_shown`. */
  action: InsufficientCreditsAction;
}) => {
  const { credits: available, isLoading } = useCreditsBalance();
  const t = useTranslations("components.ui.Alert");
  const posthog = usePostHog();

  // Default to 0 while loading to show the "not enough credits" state
  const availableCredits = available ?? 0;

  // Fire once each time the red banner appears, not on every change to
  // `required` while it stays up. Skipped while the balance loads, when the
  // banner shows red only because the balance defaults to 0.
  const isShort = !isLoading && required > 0 && availableCredits < required;
  const wasShortRef = useRef(false);
  useEffect(() => {
    const wasShort = wasShortRef.current;
    wasShortRef.current = isShort;
    if (!isShort || wasShort) return;
    posthog?.capture(ANALYTICS_EVENTS.insufficientCreditsShown, {
      action_attempted: action,
      credits_needed: required,
      credit_balance: availableCredits,
    });
  }, [isShort, posthog, action, required, availableCredits]);

  return (
    <Transition mounted={required > 0} transition="pop">
      {(styles) =>
        availableCredits < required ? (
          <Alert
            title={t("notEnough")}
            icon={<IconCoinOff />}
            style={styles}
            color="red"
          >
            <Group>
              <Text>
                {t("needCredits", { required, available: availableCredits })}
              </Text>

              <TopUpCreditsButton
                w="fit-content"
                color="green.9"
                trigger="insufficient_credits"
              />
            </Group>
          </Alert>
        ) : (
          <Alert
            title={
              <Text>
                {t("costCredits", { required, available: availableCredits })}
              </Text>
            }
            icon={<IconCoins />}
            style={styles}
            color="yellow"
          />
        )
      }
    </Transition>
  );
};

export const AlertError = ({
  title,
  description,
  children,
  ...rest
}: CustomAlertProps) => {
  const [opened, setOpened] = useState(true);
  const withCloseButton = rest.withCloseButton ?? false;

  if (!opened) {
    return null;
  }

  return (
    <Alert
      variant="light"
      color="red"
      title={title}
      icon={<IconAlertCircle />}
      withCloseButton={withCloseButton}
      onClose={() => setOpened(false)}
      {...rest}
    >
      {description ? <Text size="md">{description}</Text> : children}
    </Alert>
  );
};

export const AlertDanger = ({
  title,
  description,
  children,
  ...rest
}: CustomAlertProps) => {
  const [opened, setOpened] = useState(true);
  const withCloseButton = rest.withCloseButton ?? false;

  if (!opened) {
    return null;
  }

  return (
    <Alert
      variant="light"
      color="red"
      title={title}
      icon={<IconAlertTriangle />}
      withCloseButton={withCloseButton}
      onClose={() => setOpened(false)}
      {...rest}
    >
      {description ? <Text size="md">{description}</Text> : children}
    </Alert>
  );
};

//Not currently used, but might be useful in the future
export const AlertSuccess = ({
  title,
  description,
  children,
  ...rest
}: CustomAlertProps) => {
  const [opened, setOpened] = useState(true);
  const withCloseButton = rest.withCloseButton ?? false;

  if (!opened) {
    return null;
  }

  return (
    <Alert
      variant="light"
      color="green"
      title={title}
      icon={<IconCircleCheck />}
      withCloseButton={withCloseButton}
      onClose={() => setOpened(false)}
      {...rest}
    >
      {description ? <Text size="md">{description}</Text> : children}
    </Alert>
  );
};
